<template>
  <div class="na-table-wrapper" v-if="filledHours && filledHours.length">
    <div class="na-table-header" @click="collapsed = !collapsed">
      <div class="na-table-header-title">
        <font-awesome-icon icon="fa fa-table" class="na-table-header-icon" />
        <span>Histórico</span>
      </div>
      <div class="na-table-header-toggle">
        <font-awesome-icon
          icon="fa fa-chevron-down"
          :class="['na-table-arrow', { collapsed: collapsed }]"
        />
      </div>
    </div>
    <table v-show="!collapsed" class="na-table">
      <thead>
        <tr>
          <th v-for="col in columns" :key="col.key" :style="col.width ? { width: col.width } : {}">
            {{ col.label }}
          </th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="(row, i) in filledHours" :key="i" :class="{ 'na-row-offline': row._isOffline }">
          <td v-for="col in columns" :key="col.key">
            <template v-if="row._isOffline && col.key !== 'hora'">
              <span v-if="col === columns[1]" class="na-offline-label" :colspan="columns.length - 1">{{ rotuloLacuna(row) }}</span>
              <span v-else></span>
            </template>
            <template v-else>
              <img v-if="col.imageSrc && !row._isOffline" :src="col.imageSrc(row)" class="na-table-icon" alt="" />
              <span v-else>{{ formatCell(row, col) }}</span>
            </template>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script>
export default {
  name: "NewAsideDataTable",
  props: {
    hours: { type: Array, default: () => [] },
    columns: {
      type: Array,
      default: () => [
        { key: "hora", label: "hora", format: "time", width: "22%" },
        { key: "min", label: "mínima" },
        { key: "avg", label: "média" },
        { key: "max", label: "máxima" },
      ],
    },
    /**
     * Estações de terceiros publicam em janelas próprias (30min–1h), então a hora corrente
     * fica vazia por vários minutos em operação normal. Chamar isso de "Offline" alarma sem
     * motivo. Com esta prop ligada, a hora atual e as futuras viram "Aguardando leitura" —
     * horas passadas sem dado continuam como offline, porque aí a falha é real.
     */
    pendingLabel: { type: Boolean, default: false },
  },
  data() {
    return {
      collapsed: false,
    };
  },
  computed: {
    filledHours() {
      if (!this.hours || !this.hours.length) return [];

      // Detectar quais horas existem nos dados
      const hoursMap = {};
      let dateRef = null;
      this.hours.forEach(h => {
        let hourNum = null;
        if (h.hour != null) {
          hourNum = h.hour;
        } else if (h.dateTime) {
          // Extrair hora sem converter timezone — pegar direto da string ISO
          const match = String(h.dateTime).match(/T(\d{2}):/);
          hourNum = match ? parseInt(match[1]) : new Date(h.dateTime).getHours();
        }
        if (hourNum != null) {
          hoursMap[hourNum] = h;
        }
        if (!dateRef && h.dateTime) {
          dateRef = String(h.dateTime).substring(0, 10); // "2026-04-13"
        }
      });

      // Se não conseguiu determinar nenhuma hora, retorna os dados originais
      const hourKeys = Object.keys(hoursMap).map(Number);
      if (!hourKeys.length) return this.hours;

      // Preencher de 0 até:
      // - Se é hoje: até a hora atual (não mostra horas futuras)
      // - Se é dia passado: até 23 (dia completo, todas as horas sem dados = offline)
      // Data LOCAL: toISOString() é UTC e, depois das 21h no Brasil, já devolvia o dia
      // seguinte — o dia corrente passava a ser tratado como encerrado (horas "Offline").
      const now = new Date();
      const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
      const isToday = dateRef === today;
      const lastDataHour = Math.max(...hourKeys);
      const maxHour = isToday ? Math.max(lastDataHour, now.getHours()) : 23;
      const filled = [];
      for (let h = 0; h <= maxHour; h++) {
        if (hoursMap[h]) {
          filled.push(hoursMap[h]);
        } else {
          filled.push({
            hour: h,
            dateTime: dateRef ? `${dateRef}T${String(h).padStart(2, '0')}:00:00` : null,
            _isOffline: true,
            // Ainda pode receber leitura: a hora corrente e as adiante, e — para fontes que
            // publicam com atraso (pendingLabel) — as horas depois do último dado recebido.
            // Um buraco ANTES do último dado é falha real e continua "Offline".
            _isPending: isToday && (h >= now.getHours() || h > lastDataHour)
          });
        }
      }
      return filled;
    },
  },
  methods: {
    rotuloLacuna(row) {
      return this.pendingLabel && row._isPending ? "Aguardando leitura" : "Offline";
    },

    formatCell(row, col) {
      if (col.format === "time") {
        const dt = row.dateTime || row.data;
        if (!dt) return "--";
        const d = new Date(dt);
        return d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
      }
      if (col.formatter) {
        return col.formatter(row);
      }
      const val = row[col.key];
      if (val == null) return "--";
      if (typeof val === "number") {
        return col.decimals != null ? val.toFixed(col.decimals) : val;
      }
      return val;
    },
  },
};
</script>

<style scoped>
.na-table-wrapper {
  background: #fff;
  border-radius: 10px;
  overflow: hidden;
  margin-top: 8px;
}

.na-table-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 12px;
  cursor: pointer;
  user-select: none;
}

.na-table-header-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
  font-size: 14px;
  color: #333;
}

.na-table-header-icon {
  color: #006035;
  font-size: 14px;
}

.na-table-header-toggle {
  display: flex;
  align-items: center;
  gap: 8px;
}

.na-table-arrow {
  font-size: 12px;
  color: #666;
  transition: transform 0.3s;
}

.na-table-arrow.collapsed {
  transform: rotate(-90deg);
}

.na-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
}

.na-table thead {
  background-color: #53703c;
  color: #fff;
}

.na-table th {
  padding: 8px 6px;
  text-align: center;
  font-weight: 600;
  font-size: 12px;
}

.na-table td {
  padding: 6px;
  text-align: center;
  font-size: 12px;
  color: #333;
}

.na-table tbody tr:nth-child(even) {
  background-color: #f5f5f5;
}

.na-table tbody tr:nth-child(odd) {
  background-color: #fff;
}

.na-table-icon {
  width: 20px;
  height: 20px;
  object-fit: contain;
}

.na-row-offline td {
  color: #B0BEC5;
}

.na-offline-label {
  font-size: 11px;
  font-weight: 600;
  color: #B0BEC5;
  font-style: italic;
}
</style>
